import { get } from 'lodash'

/**
 * Odoo の応答本文からエラーの文を取り出す。無ければ `null`。
 *
 * ⚠️Odoo の `type='json'` ルートは**例外でも HTTP 200** を返し、理由を
 * 本文の `error`(JSON-RPC: `{error: {message, data: {message}}}`)に入れる。
 * axios は 200 を reject しないので、これを見ずに `res.data.result` だけを
 * 読むと `undefined` になり、画面は失敗を**別の言葉**に読み替える —
 * 「保管期間を過ぎている可能性があります」(ダウンロード)、
 * 「再開しました」(止まった日付の再開・成功通知!)など(2026-09-16)。
 *
 * ext_edwin の専用ルートはさらに `{result: {error}}` / `{errors}` の形でも
 * 失敗を返す(`controllers/edwin_linkage_settings.py` の冒頭の注記)。
 * どの形でも1本で読めるようにする。
 */
export function odooErrorMessage(payload: unknown): string | null {
  const errors = get(payload, 'errors')
  if (errors) {
    return Array.isArray(errors) ? errors.join('\n') : String(errors)
  }
  const error = get(payload, 'error')
  if (!error) return null
  if (typeof error === 'string') return error
  return (
    get(error, 'data.message') || get(error, 'message') || JSON.stringify(error)
  )
}

/**
 * 失敗した呼び出し(axios が投げた例外)から、サーバーが本文に書いた理由を
 * 取り出す。
 *
 * ⚠️HTTP 401/404 は ``Fetcher`` の interceptor が axios の例外のまま返すので、
 * ``message`` は「Request failed with status code 401」でしかない。理由は
 * 本文(``response.data``)にある ―― ``make_permission_denied`` は
 * ``{error: '…'}``、``make_error`` は ``{error: {code, message}}``。
 * ダウンロードは ``responseType: 'blob'`` なので本文も blob の中の JSON。
 * 400 は interceptor が ``{code, message}``(message = 本文の理由)に
 * 詰め替えて投げるので ``message`` をそのまま使う
 * (2026-09-24 レビュー第2周: 汎用の文面ではなく理由を画面に出す)。
 */
export async function reasonOf(err: unknown): Promise<string> {
  let body: unknown = get(err, 'response.data')
  if (body && typeof get(body, 'text') === 'function') {
    try {
      body = JSON.parse(await (body as Blob).text())
    } catch {
      body = null
    }
  }
  const fromBody = odooErrorMessage(body)
  if (fromBody) return fromBody
  const message = get(err, 'message')
  return typeof message === 'string' && message ? message : String(err)
}

/**
 * ``ApiCall`` の呼び出しを包み、失敗をサーバーの理由つきの ``Error`` にする。
 * AS400連携の画面(出力ファイル・受注登録)が共有する。
 */
export async function callApi<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request()
  } catch (err) {
    throw new Error(await reasonOf(err))
  }
}
